import type { BasketUpsellResponse } from '@library/api-menu-experience'

import {
  getFirstProductCategoryAtLimit,
  getProductItemLimitReached,
  productsOverallLimitReached,
} from 'utils/basketProductLimits'

export type LimitReached =
  | false
  | {
      type: 'box' | 'item' | 'category'
      value: string | number
    }

export type LocalBasket = {
  products: Record<string, number>
  gifts: Record<string, number>
}

type ProductLimitProduct = {
  boxLimit: number
  categories: { id: string }[]
}

type ProductLimitCategory = {
  id: string
  title: string
  boxLimit: number
}

export type ProductLimitMaps = {
  categories: Record<string, ProductLimitCategory>
  products: Record<string, ProductLimitProduct>
}

export const getImmutableRecord = (value: unknown): Record<string, number> =>
  typeof (value as { toJS?: () => Record<string, number> } | undefined)?.toJS === 'function'
    ? (value as { toJS: () => Record<string, number> }).toJS()
    : {}

export const buildProductLimitMaps = ({
  categories,
  products,
  maxProductsPerBox,
}: BasketUpsellResponse): ProductLimitMaps => {
  // Basket upsell products are not loaded into the global products Redux slice, so build the
  // product/category shape expected by the shared basket product limit helpers from the response.
  const categoriesByProductId = categories.reduce<Record<string, { id: string }[]>>(
    (acc, category) => {
      category.products.forEach(({ id }) => {
        acc[id] = [...(acc[id] ?? []), { id: category.id }]
      })

      return acc
    },
    {},
  )

  return {
    categories: categories.reduce<Record<string, ProductLimitCategory>>(
      (acc, category) => ({
        ...acc,
        [category.id]: {
          id: category.id,
          title: category.title,
          boxLimit: category.boxLimit ?? maxProductsPerBox,
        },
      }),
      {},
    ),
    products: Object.values(products).reduce<Record<string, ProductLimitProduct>>(
      (acc, product) => ({
        ...acc,
        [product.id]: {
          boxLimit: product.boxLimit ?? maxProductsPerBox,
          categories: categoriesByProductId[product.id] ?? [],
        },
      }),
      {},
    ),
  }
}

export const getUpsellProductLimitReached = (
  productId: string,
  basket: LocalBasket,
  upsellResponse: BasketUpsellResponse,
  productLimitMaps: ProductLimitMaps,
): LimitReached => {
  // Reuse the existing basket product limit rules with the upsell response's dynamic box limit.
  if (productsOverallLimitReached(basket, true, upsellResponse.maxProductsPerBox)) {
    return { type: 'box', value: upsellResponse.maxProductsPerBox }
  }

  const productItemAtLimit = getProductItemLimitReached(
    productId,
    basket,
    productLimitMaps.products,
  )
  if (productItemAtLimit) {
    return { type: 'item', value: productItemAtLimit }
  }

  const categoryAtLimit = getFirstProductCategoryAtLimit(
    productId,
    basket,
    productLimitMaps.products,
    productLimitMaps.categories,
  )
  if (categoryAtLimit) {
    return { type: 'category', value: categoryAtLimit }
  }

  return false
}
